import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Truck, MapPin, Power, Navigation, RefreshCw, Fuel, Gauge, Clock, Thermometer, Battery, Route, ChevronDown, ChevronRight } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fix for default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const createIcon = (color) => new L.Icon({
  iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${color}.png`,
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const movingIcon = createIcon('green');
const idleIcon = createIcon('orange');
const parkedIcon = createIcon('grey');

export default function TitanGPSPage() {
  const [vehicles, setVehicles] = useState([]);
  const [stats, setStats] = useState({ count: 0, moving: 0, ignition_on: 0, parked: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [mapCenter, setMapCenter] = useState([56.25, -120.85]);
  const [refreshing, setRefreshing] = useState(false);
  const [showParked, setShowParked] = useState(false);
  const [showIdle, setShowIdle] = useState(true);
  const [landmarkEvents, setLandmarkEvents] = useState({ events: [], summary: {}, active_landmarks: [] });
  const [showLandmarkEvents, setShowLandmarkEvents] = useState(true);
  const [vehiclesInLandmarks, setVehiclesInLandmarks] = useState({ vehicles: [], by_landmark: {} });

  const fetchData = async () => {
    setRefreshing(true);
    try {
      const token = localStorage.getItem('vaultai_token');
      if (!token) {
        setError('Not authenticated');
        return;
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch vehicles and landmark events in parallel
      const [vehiclesResponse, landmarkResponse] = await Promise.all([
        axios.get(`${API}/titan-gps/vehicles`, { headers }),
        axios.get(`${API}/titan-gps/landmark-events?limit=30`, { headers }).catch(() => ({ data: { events: [] } }))
      ]);

      if (vehiclesResponse.data.success) {
        const vehicleData = vehiclesResponse.data.vehicles || [];
        setVehicles(vehicleData);
        setStats({
          count: vehiclesResponse.data.count || 0,
          moving: vehiclesResponse.data.moving || 0,
          ignition_on: vehiclesResponse.data.ignition_on || 0,
          parked: vehiclesResponse.data.parked || 0
        });
        
        // Extract vehicles in landmarks
        const inLandmarks = vehicleData.filter(v => v.insideLandmark && v.insideLandmark.trim());
        const byLandmark = {};
        inLandmarks.forEach(v => {
          const landmark = v.insideLandmark.trim();
          if (!byLandmark[landmark]) byLandmark[landmark] = [];
          byLandmark[landmark].push(v);
        });
        setVehiclesInLandmarks({ vehicles: inLandmarks, by_landmark: byLandmark });
        
        if (vehicleData.length > 0) {
          const validVehicle = vehicleData.find(v => v.latitude && v.longitude && v.latitude !== 0);
          if (validVehicle) {
            setMapCenter([validVehicle.latitude, validVehicle.longitude]);
          }
        }
        setError(null);
      } else {
        setError(vehiclesResponse.data.error || 'Failed to fetch data');
      }
      
      // Set landmark events
      if (landmarkResponse.data.success) {
        setLandmarkEvents(landmarkResponse.data);
      }

      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to fetch Titan GPS data:', err);
      setError(`Failed to connect: ${err.message}`);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 120000);
    return () => clearInterval(interval);
  }, []);

  const getTimeSince = (utcString) => {
    if (!utcString) return '-';
    const date = new Date(utcString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${Math.floor(diffHours / 24)}d ago`;
  };

  const getHeadingDirection = (degrees) => {
    if (degrees === null || degrees === undefined) return '';
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(degrees / 45) % 8;
    return directions[index];
  };

  const getVehicleIcon = (vehicle) => {
    if (vehicle.speedKph > 0) return movingIcon;
    if (vehicle.ignitionStatus === 'On') return idleIcon;
    return parkedIcon;
  };

  // Group vehicles by status
  const movingVehicles = vehicles.filter(v => v.speedKph > 0);
  const idleVehicles = vehicles.filter(v => v.speedKph === 0 && v.ignitionStatus === 'On');
  const parkedVehicles = vehicles.filter(v => v.speedKph === 0 && v.ignitionStatus !== 'On');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-slate-600">Connecting to Titan GPS...</span>
      </div>
    );
  }

  // Detailed vehicle card for moving vehicles
  const MovingVehicleCard = ({ vehicle }) => (
    <div className="p-3 rounded-lg border bg-green-50 border-green-200">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse"></span>
          <span className="font-bold text-slate-900">
            {vehicle.deviceUnitNumber || 'N/A'}
          </span>
          <span className="text-green-700 font-semibold">
            {Math.round(vehicle.speedKph)} km/h {getHeadingDirection(vehicle.headingDegrees)}
          </span>
        </div>
        <span className="text-xs text-slate-500">{getTimeSince(vehicle.eventTimeUTC)}</span>
      </div>
      
      <p className="text-sm font-medium text-slate-800 mb-2">{vehicle.deviceName}</p>
      
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <div className="flex items-center gap-1 text-slate-600">
          <Route className="h-3 w-3" />
          <span>{vehicle.odometerKm ? `${Math.round(vehicle.odometerKm).toLocaleString()} km` : '-'}</span>
        </div>
        <div className="flex items-center gap-1 text-slate-600">
          <Clock className="h-3 w-3" />
          <span>{vehicle.odometerHours ? `${Math.round(vehicle.odometerHours).toLocaleString()} hrs` : '-'}</span>
        </div>
        <div className="flex items-center gap-1 text-slate-600">
          <Fuel className="h-3 w-3" />
          <span>{vehicle.fuelLevelPercentage > 0 ? `${vehicle.fuelLevelPercentage}%` : '-'}</span>
        </div>
        <div className="flex items-center gap-1 text-slate-600">
          <Truck className="h-3 w-3" />
          <span>{vehicle.group || '-'}</span>
        </div>
      </div>
      
      {vehicle.address && (
        <p className="text-xs text-slate-500 mt-2 truncate" title={vehicle.address}>
          📍 {vehicle.address}
        </p>
      )}
    </div>
  );

  // Compact card for idle/parked
  const VehicleCard = ({ vehicle, type }) => {
    const bgColor = type === 'idle' ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200';
    const dotColor = type === 'idle' ? 'bg-amber-500' : 'bg-slate-400';
    
    return (
      <div className={`p-2.5 rounded-lg border ${bgColor}`}>
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${dotColor}`}></span>
            <span className="font-semibold text-sm text-slate-900">
              {vehicle.deviceUnitNumber || 'N/A'}
            </span>
          </div>
          <span className="text-xs text-slate-500">{getTimeSince(vehicle.eventTimeUTC)}</span>
        </div>
        
        <p className="text-xs text-slate-700 truncate mb-1.5" title={vehicle.deviceName}>
          {vehicle.deviceName}
        </p>
        
        <div className="grid grid-cols-2 gap-1 text-xs text-slate-500">
          <span title="Odometer">{vehicle.odometerKm ? `${Math.round(vehicle.odometerKm/1000)}k km` : '-'}</span>
          <span title="Engine Hours">{vehicle.odometerHours ? `${Math.round(vehicle.odometerHours)} hrs` : '-'}</span>
          <span title="Fuel">{vehicle.fuelLevelPercentage > 0 ? `⛽ ${vehicle.fuelLevelPercentage}%` : ''}</span>
          <span title="Group">{vehicle.group || ''}</span>
        </div>
        
        {vehicle.insideLandmark && (
          <p className="text-xs text-slate-500 mt-1 truncate">📍 {vehicle.insideLandmark}</p>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="titan-gps-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Titan GPS</h1>
          <p className="text-sm text-slate-500">Real-time fleet tracking • Updates every 2 min</p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <span className="text-xs text-slate-500">{lastUpdated.toLocaleTimeString()}</span>
          )}
          <button
            onClick={fetchData}
            disabled={refreshing}
            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-3">
        <Card className="bg-blue-500 text-white">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-xs">Total</p>
                <p className="text-xl font-bold">{vehicles.length}</p>
              </div>
              <Truck className="h-6 w-6 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-green-500 text-white">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-xs">Moving</p>
                <p className="text-xl font-bold">{movingVehicles.length}</p>
              </div>
              <Navigation className="h-6 w-6 text-green-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-amber-500 text-white">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-xs">Idle</p>
                <p className="text-xl font-bold">{idleVehicles.length}</p>
              </div>
              <Power className="h-6 w-6 text-amber-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-500 text-white">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-200 text-xs">Parked</p>
                <p className="text-xl font-bold">{parkedVehicles.length}</p>
              </div>
              <MapPin className="h-6 w-6 text-slate-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Vehicles at Landmarks & Recent Events */}
      {(Object.keys(vehiclesInLandmarks.by_landmark).length > 0 || landmarkEvents.events?.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Currently at Landmarks */}
          {Object.keys(vehiclesInLandmarks.by_landmark).length > 0 && (
            <Card className="border-green-200 bg-green-50/30">
              <CardHeader className="pb-2 py-3">
                <CardTitle className="flex items-center justify-between text-base">
                  <div className="flex items-center gap-2 text-green-800">
                    <MapPin className="h-4 w-4" />
                    Vehicles at Landmarks
                    <Badge variant="outline" className="ml-2 bg-green-100 text-green-700 border-green-300">
                      {vehiclesInLandmarks.vehicles.length} vehicles
                    </Badge>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0">
                <div className="space-y-3 max-h-[300px] overflow-y-auto">
                  {Object.entries(vehiclesInLandmarks.by_landmark).map(([landmark, vehicles]) => (
                    <div key={landmark} className="p-2 rounded-lg bg-white border border-green-200">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg">📍</span>
                        <span className="font-semibold text-green-800">{landmark}</span>
                        <Badge variant="outline" className="text-xs bg-green-50 text-green-600 border-green-200">
                          {vehicles.length}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {vehicles.map((v, idx) => (
                          <span 
                            key={idx} 
                            className="px-2 py-0.5 text-xs rounded bg-green-100 text-green-700"
                            title={v.deviceName}
                          >
                            {v.deviceUnitNumber || v.deviceName}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent Landmark Events */}
          {landmarkEvents.events?.length > 0 && (
            <Card>
              <CardHeader className="pb-2 py-3">
                <button 
                  onClick={() => setShowLandmarkEvents(!showLandmarkEvents)}
                  className="flex items-center justify-between w-full text-left"
                >
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Clock className="h-4 w-4 text-blue-600" />
                    Recent Landmark Activity
                    <Badge variant="outline" className="ml-2 bg-blue-50 text-blue-600 border-blue-200">
                      {landmarkEvents.summary?.entries || 0} arrivals
                    </Badge>
                    <Badge variant="outline" className="bg-amber-50 text-amber-600 border-amber-200">
                      {landmarkEvents.summary?.exits || 0} departures
                    </Badge>
                  </CardTitle>
                  {showLandmarkEvents ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
              </CardHeader>
              {showLandmarkEvents && (
                <CardContent className="p-3 pt-0">
                  <div className="space-y-1 max-h-[300px] overflow-y-auto">
                    {landmarkEvents.events.slice(0, 20).map((event, idx) => (
                      <div 
                        key={idx} 
                        className={`flex items-center justify-between p-2 rounded text-sm ${
                          event.type === 'entry' 
                            ? 'bg-green-500/20 border border-green-500/30' 
                            : 'bg-amber-500/20 border border-amber-500/30'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className={event.type === 'entry' ? 'text-green-500' : 'text-amber-500'}>
                            {event.type === 'entry' ? '📍' : '🚚'}
                          </span>
                          <span className={`font-bold ${event.type === 'entry' ? 'text-green-400' : 'text-amber-400'}`}>
                            {event.device_name}
                          </span>
                          <span className="text-slate-300">
                            {event.type === 'entry' ? 'arrived at' : 'departed from'}
                          </span>
                          <span className="text-white font-medium">
                            {event.landmark_name}
                          </span>
                          {event.previous_landmark && (
                            <span className="text-slate-400 text-xs">
                              (from {event.previous_landmark})
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-slate-400">
                          {getTimeSince(event.created_at)}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          )}
        </div>
      )}

      {/* Map and Moving Vehicles */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2 py-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <MapPin className="h-4 w-4" />
              Fleet Map
              <span className="text-xs font-normal text-slate-500 ml-2">
                <span className="inline-block w-2 h-2 bg-green-500 rounded-full mr-1"></span>Moving
                <span className="inline-block w-2 h-2 bg-amber-500 rounded-full ml-2 mr-1"></span>Idle
                <span className="inline-block w-2 h-2 bg-slate-400 rounded-full ml-2 mr-1"></span>Parked
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="h-[600px] rounded-b-lg overflow-hidden">
              <MapContainer
                center={mapCenter}
                zoom={8}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={true}
              >
                <TileLayer
                  attribution='&copy; OpenStreetMap'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {vehicles.filter(v => v.latitude && v.longitude && v.latitude !== 0).map((vehicle, idx) => (
                  <Marker
                    key={vehicle.deviceID || idx}
                    position={[vehicle.latitude, vehicle.longitude]}
                    icon={getVehicleIcon(vehicle)}
                  >
                    <Popup>
                      <div className="text-sm min-w-[200px]">
                        <p className="font-bold text-base">{vehicle.deviceUnitNumber} - {vehicle.deviceName}</p>
                        <p className="text-slate-600 mb-2">{vehicle.assetType} • {vehicle.group}</p>
                        <div className="space-y-1 text-xs">
                          <p><strong>Status:</strong> {vehicle.speedKph > 0 ? `Moving ${Math.round(vehicle.speedKph)} km/h ${getHeadingDirection(vehicle.headingDegrees)}` : vehicle.ignitionStatus === 'On' ? 'Idle' : 'Parked'}</p>
                          <p><strong>Odometer:</strong> {vehicle.odometerKm ? `${Math.round(vehicle.odometerKm).toLocaleString()} km` : '-'}</p>
                          <p><strong>Engine Hours:</strong> {vehicle.odometerHours ? `${Math.round(vehicle.odometerHours).toLocaleString()} hrs` : '-'}</p>
                          {vehicle.fuelLevelPercentage > 0 && <p><strong>Fuel:</strong> {vehicle.fuelLevelPercentage}%</p>}
                          {vehicle.deviceVin && <p><strong>VIN:</strong> {vehicle.deviceVin}</p>}
                        </div>
                        <p className="text-xs text-slate-500 mt-2">{vehicle.address}</p>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </CardContent>
        </Card>

        {/* Moving Vehicles */}
        <Card className="bg-green-50/50">
          <CardHeader className="pb-2 py-3">
            <CardTitle className="flex items-center gap-2 text-base text-green-800">
              <Navigation className="h-4 w-4" />
              Moving Now ({movingVehicles.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-0">
            {movingVehicles.length > 0 ? (
              <div className="space-y-2 max-h-[550px] overflow-y-auto">
                {movingVehicles.map((v, idx) => (
                  <MovingVehicleCard key={v.deviceID || idx} vehicle={v} />
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500 text-center py-8">No vehicles moving</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Idle Vehicles */}
      {idleVehicles.length > 0 && (
        <Card>
          <CardHeader className="pb-2 py-3">
            <button 
              onClick={() => setShowIdle(!showIdle)}
              className="flex items-center justify-between w-full text-left"
            >
              <CardTitle className="flex items-center gap-2 text-base">
                <Power className="h-4 w-4 text-amber-600" />
                Idle - Ignition On ({idleVehicles.length})
              </CardTitle>
              <span className="text-sm text-slate-500">{showIdle ? '▼' : '▶'}</span>
            </button>
          </CardHeader>
          {showIdle && (
            <CardContent className="p-3 pt-0">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
                {idleVehicles.map((v, idx) => (
                  <VehicleCard key={v.deviceID || idx} vehicle={v} type="idle" />
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Parked Vehicles */}
      <Card>
        <CardHeader className="pb-2 py-3">
          <button 
            onClick={() => setShowParked(!showParked)}
            className="flex items-center justify-between w-full text-left"
          >
            <CardTitle className="flex items-center gap-2 text-base">
              <MapPin className="h-4 w-4 text-slate-500" />
              Parked ({parkedVehicles.length})
            </CardTitle>
            <span className="text-sm text-slate-500">{showParked ? '▼ Hide' : '▶ Show'}</span>
          </button>
        </CardHeader>
        {showParked && (
          <CardContent className="p-3 pt-0">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 max-h-[650px] overflow-y-auto">
              {parkedVehicles.map((v, idx) => (
                <VehicleCard key={v.deviceID || idx} vehicle={v} type="parked" />
              ))}
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
